import axios, {
  AxiosError,
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { getAuth } from "firebase/auth";
import { getSession, signOut } from "next-auth/react";
import * as Sentry from "@sentry/nextjs";

import { performTokenRefresh } from "./token-refresh";
import { REQUEST_ID_HEADER } from "./captureApiError";

// Extended config type to store span reference
interface AxiosConfigWithSpan extends InternalAxiosRequestConfig {
  _sentrySpan?: ReturnType<typeof Sentry.startInactiveSpan>;
  _sentryStartTime?: number;
}

/**
 * Generate a correlation id for an outgoing request. Prefers crypto.randomUUID
 * (browser + Node 19+) with a timestamp/random fallback for older runtimes.
 */
function generateRequestId(): string {
  try {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
      return crypto.randomUUID();
    }
  } catch {
    // fall through to the manual id below
  }
  return `req-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Ensure the request carries an `X-Request-ID` header so the backend can echo
 * it and tag it on its logs/Sentry events — letting us trace a user's error
 * toast to the exact backend request. Returns the id (existing or generated).
 */
function ensureRequestId(config: AxiosConfigWithSpan): string {
  const existing = config.headers?.[REQUEST_ID_HEADER];
  if (typeof existing === "string" && existing) return existing;
  const id = generateRequestId();
  if (config.headers) {
    config.headers[REQUEST_ID_HEADER] = id;
  }
  return id;
}

/**
 * Creates a Sentry span for an outgoing HTTP request.
 * The span is started but not ended - call endSpan in the response interceptor.
 */
function startHttpSpan(config: AxiosConfigWithSpan): void {
  const requestId = ensureRequestId(config);

  if (config._sentrySpan) {
    return;
  }

  const method = config.method?.toUpperCase() ?? "GET";
  const url = config.url ?? "unknown";
  const baseURL = config.baseURL ?? "";
  const fullUrl = url.startsWith("http") ? url : `${baseURL}${url}`;

  let urlPath: string;
  try {
    const parsedUrl = new URL(fullUrl);
    urlPath = parsedUrl.pathname;
  } catch {
    urlPath = url;
  }

  const span = Sentry.startInactiveSpan({
    name: `${method} ${urlPath}`,
    op: "http.client",
    attributes: {
      "http.request.method": method,
      "http.url": fullUrl,
      "server.address": baseURL,
      request_id: requestId,
    },
  });

  config._sentrySpan = span;
  config._sentryStartTime = Date.now();
}

/**
 * Ends the Sentry span for an HTTP request with success status.
 */
function endHttpSpanSuccess(response: AxiosResponse): void {
  const config = response.config as AxiosConfigWithSpan;
  const span = config._sentrySpan;

  if (span) {
    span.setAttribute("http.response.status_code", response.status);
    span.setStatus({ code: 1, message: "ok" }); // SpanStatusCode.OK = 1
    span.end();
  }
}

/**
 * Ends the Sentry span for an HTTP request with error status.
 */
function endHttpSpanError(error: AxiosError): void {
  const config = error.config as AxiosConfigWithSpan | undefined;
  const span = config?._sentrySpan;

  if (span) {
    const statusCode = error.response?.status;
    if (statusCode) {
      span.setAttribute("http.response.status_code", statusCode);
    }
    span.setStatus({ code: 2, message: error.message }); // SpanStatusCode.ERROR = 2
    span.end();
  }
}

// Base axios instance for Next.js API routes
export const nextApiClient = axios.create({
  baseURL: "/api",
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 15000,
});

// Add Sentry span interceptors for Next.js API client
nextApiClient.interceptors.request.use((config) => {
  startHttpSpan(config as AxiosConfigWithSpan);
  return config;
});

nextApiClient.interceptors.response.use(
  (response) => {
    endHttpSpanSuccess(response);
    return response;
  },
  (error: AxiosError) => {
    endHttpSpanError(error);
    return Promise.reject(error);
  },
);

// Base axios instance for external curriculum API (V1 - no /api prefix)
export const curriculumApiClient = axios.create({
  baseURL: process.env.NEXT_PUBLIC_CURRICULUM_API_URL,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 10000,
});

// Track if a refresh is in progress to prevent concurrent refresh calls
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

type RetryConfig = AxiosConfigWithSpan & { _retry?: boolean };

/**
 * Shared 401 → refresh-token → retry handler used by both the v1 and
 * v2 curriculum API clients. Lifts the duplication out of each
 * interceptor and keeps the in-flight refresh queue truly shared so a
 * v1 and v2 request that 401 in parallel only fire one /auth/refresh
 * round-trip.
 */
async function refreshAndRetry<T>(
  client: AxiosInstance,
  error: AxiosError,
): Promise<AxiosResponse<T>> {
  const originalRequest = error.config as RetryConfig;
  const isAlreadyRetried = originalRequest._retry;
  if (error.response?.status !== 401 || isAlreadyRetried) {
    endHttpSpanError(error);
    throw error;
  }
  if (isRefreshing) {
    const token = await new Promise<unknown>((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    });
    if (originalRequest.headers) {
      originalRequest.headers.Authorization = `Bearer ${token}`;
    }
    return client(originalRequest);
  }
  originalRequest._retry = true;
  isRefreshing = true;
  try {
    const newAccessToken = await performTokenRefresh();
    if (originalRequest.headers) {
      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
    }
    processQueue(null, newAccessToken);
    return client(originalRequest);
  } catch (refreshError) {
    processQueue(refreshError as Error, null);
    if (
      axios.isAxiosError(refreshError) &&
      refreshError.response?.status === 401
    ) {
      await signOut({ redirect: true, callbackUrl: "/auth/login" });
    }
    throw refreshError;
  } finally {
    isRefreshing = false;
  }
}

// Base axios instance for curriculum API v2
export const curriculumApiV2Client = axios.create({
  baseURL: `${process.env.NEXT_PUBLIC_CURRICULUM_API_URL}/api/v2`,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 10000,
});

// Cache the most recent v2 access token so callers can issue a
// keepalive fetch on `pagehide` without awaiting `getSession()` —
// `getSession` is async, but the page is unloading and there's no
// time to await anything. The token is refreshed on every v2 request
// interceptor run, so it's effectively never older than the most
// recent network round-trip from this tab.
let _cachedCurriculumV2Token: string | null = null;

/**
 * Synchronously read the last access token used by the curriculum v2
 * client. Returns null if no v2 request has run yet in this session.
 *
 * Intended for unload-time save flushes only — every other path should
 * go through the axios client so token refresh / 401 retry kick in.
 */
export function getCachedCurriculumV2Token(): string | null {
  return _cachedCurriculumV2Token;
}

export function getCurriculumV2BaseUrl(): string {
  return `${process.env.NEXT_PUBLIC_CURRICULUM_API_URL}/api/v2`;
}

// Add auth token interceptor for curriculum API
curriculumApiClient.interceptors.request.use(async (config) => {
  startHttpSpan(config as AxiosConfigWithSpan);

  const session = await getSession({ broadcast: false });
  const token = session?.databaseApiAccessToken;

  config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Add response interceptor to end Sentry spans on success
curriculumApiClient.interceptors.response.use((response) => {
  endHttpSpanSuccess(response);
  return response;
}, undefined);

// Add response interceptor to handle 401 errors and refresh tokens
curriculumApiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => refreshAndRetry(curriculumApiClient, error),
);

// Add auth token interceptor for curriculum API v2
curriculumApiV2Client.interceptors.request.use(async (config) => {
  startHttpSpan(config as AxiosConfigWithSpan);

  const session = await getSession({ broadcast: false });
  const token = session?.databaseApiAccessToken;

  // Stash the token for the unload-time `keepalive` fetch path —
  // see getCachedCurriculumV2Token doc above.
  _cachedCurriculumV2Token = token ?? null;

  config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Add response interceptor to end Sentry spans for v2 client
// v2 calls use the same NextAuth `databaseApiAccessToken`, which has a
// TTL — without 401 → refresh → retry, long-lived sessions (e.g. a
// student opens a homework, walks away, returns to answer) hit 401
// mid-flow and the request just fails. The shared helper coordinates
// the in-flight refresh with v1 so a v1 + v2 401 in parallel only
// fires one /auth/refresh round-trip.
curriculumApiV2Client.interceptors.response.use(
  (response) => {
    endHttpSpanSuccess(response);
    return response;
  },
  (error: AxiosError) => refreshAndRetry(curriculumApiV2Client, error),
);

// ---------------------------------------------------------------------------
// Firebase-direct auth clients — use getIdToken() from the Firebase client SDK
// instead of session tokens. No 401 retry interceptor needed because the
// Firebase SDK handles token refresh automatically via getIdToken(true).
// ---------------------------------------------------------------------------

// Resolves once Firebase auth has completed its initial state check.
// After a page refresh, getAuth().currentUser is null until the SDK
// asynchronously restores the persisted session. Awaiting this promise
// in the request interceptor prevents requests from going out
// unauthenticated during that window.
let _firebaseAuthReady: Promise<void> | null = null;

export function getFirebaseAuthReady(): Promise<void> {
  if (_firebaseAuthReady) return _firebaseAuthReady;

  let auth: ReturnType<typeof getAuth>;
  try {
    auth = getAuth();
  } catch {
    // Firebase app not initialized yet — return a rejected promise
    // but don't cache it so the next call retries.
    return Promise.reject(new Error("Firebase auth not available yet"));
  }

  _firebaseAuthReady = new Promise<void>((resolve) => {
    const unsubscribe = auth.onAuthStateChanged(() => {
      unsubscribe();
      resolve();
    });
  });

  return _firebaseAuthReady;
}

interface FirebaseAuthInterceptorOptions {
  // When set and the request is being issued by a Firebase-authenticated
  // browser session, the interceptor rewrites `config.baseURL` to this value.
  // Requests from sessions without a Firebase client user keep the client's
  // original baseURL (e.g. a same-origin proxy path).
  directBaseURL?: string;
}

function attachFirebaseAuthInterceptors(
  client: AxiosInstance,
  options: FirebaseAuthInterceptorOptions = {},
): void {
  const { directBaseURL } = options;

  client.interceptors.request.use(async (config) => {
    try {
      await getFirebaseAuthReady();
      const user = getAuth().currentUser;
      if (user) {
        const idToken = await user.getIdToken();
        config.headers.Authorization = `Bearer ${idToken}`;
        if (directBaseURL) {
          config.baseURL = directBaseURL;
        }
        startHttpSpan(config as AxiosConfigWithSpan);
        return config;
      }
    } catch {
      // Firebase not initialized yet — fall through to session token.
    }

    // Fallback: if Firebase client has no user (e.g. email/password login
    // where only the server-side Firebase SDK was used), use the session token.
    const session = await getSession({ broadcast: false });
    const token = session?.databaseApiAccessToken;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    startHttpSpan(config as AxiosConfigWithSpan);
    return config;
  });

  client.interceptors.response.use(
    (response) => {
      endHttpSpanSuccess(response);
      return response;
    },
    async (error: AxiosError) => {
      const originalRequest = error.config as AxiosConfigWithSpan & {
        _retry?: boolean;
      };

      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;
        try {
          await getFirebaseAuthReady();
          const user = getAuth().currentUser;
          if (user) {
            const freshToken = await user.getIdToken(true);
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${freshToken}`;
            }
            return client(originalRequest);
          }
        } catch {
          // Token refresh failed (disabled account, network error, etc.)
          // Fall through to end the span and reject with the original 401 error.
        }
      }

      endHttpSpanError(error);
      return Promise.reject(error);
    },
  );
}

export const firebaseCurriculumApiClient = axios.create({
  baseURL: process.env.NEXT_PUBLIC_CURRICULUM_API_URL,
  headers: { "Content-Type": "application/json" },
  timeout: 10000,
});
attachFirebaseAuthInterceptors(firebaseCurriculumApiClient);

export const firebaseCurriculumApiV2Client = axios.create({
  baseURL: `${process.env.NEXT_PUBLIC_CURRICULUM_API_URL}/api/v2`,
  headers: { "Content-Type": "application/json" },
  timeout: 10000,
});
attachFirebaseAuthInterceptors(firebaseCurriculumApiV2Client);

// Direct medly-backend client — bypasses database-api.
//
// Dual-mode routing:
//   - Firebase-authenticated browser sessions (getAuth().currentUser is non-null)
//     send requests straight to NEXT_PUBLIC_MEDLY_BACKEND_URL with a Firebase
//     ID token as Bearer credential. The backend's FirebaseAuthentication
//     class verifies the token directly.
//   - Sessions without a Firebase client user (legacy NextAuth-only logins)
//     fall back to the Next.js API route /api/medly-backend-proxy/[...path],
//     which authenticates via NextAuth session cookie and forwards with
//     X-API-Key + X-User-Id.
//
// When NEXT_PUBLIC_MEDLY_BACKEND_URL is unset the direct path is disabled
// and every request goes through the proxy.
export const medlyBackendApiClient = axios.create({
  baseURL: "/api/medly-backend-proxy",
  headers: { "Content-Type": "application/json" },
  // Curriculum fetches with include_question_data can take >10s for large
  // subjects; allow up to 30s before aborting.
  timeout: 30000,
});
attachFirebaseAuthInterceptors(medlyBackendApiClient, {
  directBaseURL: process.env.NEXT_PUBLIC_MEDLY_BACKEND_URL,
});

// Public medly-backend client — anonymous, no Firebase / NextAuth headers.
//
// For endpoints the backend explicitly marks `@authentication_classes([])` +
// `@permission_classes([AllowAny])` (eg `/api/platform-messages/`, which the
// login surface polls before a user signs in). Hits NEXT_PUBLIC_MEDLY_BACKEND_URL
// directly with no interceptors; do NOT add interceptors here or pre-auth
// callers will be denied / 401-loop.
export const medlyBackendPublicApiClient = axios.create({
  baseURL: process.env.NEXT_PUBLIC_MEDLY_BACKEND_URL,
  headers: { "Content-Type": "application/json" },
  timeout: 10000,
});
